import React, { useEffect, useRef, useState } from 'react';
import useAutoFitScale from '../hooks/useAutoFitScale';

/**
 * Standard section wrapper — PPTX mode.
 *
 * Non-fullBleed sections render their content on a fixed 1680px design
 * canvas that is uniformly auto-scaled (up AND down) to fill the whole
 * viewport with virtually no margins — exactly like PowerPoint renders
 * a slide. FullBleed sections (videos etc.) are untouched.
 *
 * Also renders a one-shot "light-sweep reveal" spotlight.
 */
export default function SectionShell({
  id,
  children,
  className = '',
  fullBleed = false,
  centerContent = false,
  ...rest
}) {
  const ref = useRef(null);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useAutoFitScale(outerRef, innerRef, { disabled: fullBleed });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (revealed) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.18) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: [0.18, 0.3] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  return (
    <section
      ref={ref}
      id={id}
      data-section
      data-testid={`section-${id}`}
      className={`relative w-full overflow-hidden ${
        centerContent ? 'flex items-center' : ''
      } ${className}`}
      style={{ minHeight: '100vh' }}
      {...rest}
    >
      <div
        className="section-spotlight"
        data-active={revealed ? 'true' : 'false'}
        aria-hidden="true"
      />
      {fullBleed ? (
        <div className="relative w-full h-full">{children}</div>
      ) : (
        // <figure> (not <div>) so legacy `section > div` padding overrides
        // from the old web-design era cannot re-introduce margins.
        <figure ref={outerRef} className="shell-fit-outer">
          <div ref={innerRef} className="shell-fit-inner">
            {children}
          </div>
        </figure>
      )}
    </section>
  );
}
